import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import usersService from '../../../services/users.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import ClinicSettingsFormFooter from '../settings/clinicsettings/subparts/ClinicSettingsFormFooter';

const PASSWORD_REQUIREMENTS = [
    {
        key: 'length',
        label: 'At least 8 characters long',
        test: (password) => password.length >= 8,
    },
    {
        key: 'uppercase',
        label: 'Contains at least one uppercase letter',
        test: (password) => /[A-Z]/.test(password),
    },
    {
        key: 'lowercase',
        label: 'Contains at least one lowercase letter',
        test: (password) => /[a-z]/.test(password),
    },
    {
        key: 'number',
        label: 'Includes at least one number',
        test: (password) => /[0-9]/.test(password),
    },
];

const getPasswordStrength = (password) => {
    if (!password) {
        return { strength: 0, label: '', className: '' };
    }

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const classNames = ['is-very-weak', 'is-weak', 'is-fair', 'is-good', 'is-strong', 'is-very-strong'];

    return {
        strength,
        label: labels[strength],
        className: classNames[strength],
    };
};

const PasswordField = ({
    label,
    htmlFor,
    error,
    show,
    onToggle,
    register,
    validation,
    children,
}) => (
    <div className="password-update-field">
        <label className="password-update-field__label" htmlFor={htmlFor}>
            {label}
        </label>
        <div className="password-update-field__control">
            <div className="input-group input-group-sm">
                <input
                    type={show ? 'text' : 'password'}
                    className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
                    id={htmlFor}
                    autoComplete={htmlFor === 'oldPassword' ? 'current-password' : 'new-password'}
                    {...register(htmlFor, validation)}
                />
                <button
                    type="button"
                    className="btn btn-outline-secondary password-update-field__toggle"
                    onClick={onToggle}
                    aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                >
                    <i className={`bi ${show ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
            </div>
            {error && <div className="password-update-field__error">{error}</div>}
            {children}
        </div>
    </div>
);

const PasswordUpdateComponent = () => {
    const [showPassword, setShowPassword] = useState({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const userId = useEyeconxStore((state) => state.user.ID);
    const logout = useEyeconxStore((state) => state.logout);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        mode: 'onChange',
    });

    const oldPassword = watch('oldPassword');
    const newPassword = watch('newPassword');
    const passwordStrength = getPasswordStrength(newPassword);

    const requirementStatus = useMemo(
        () =>
            PASSWORD_REQUIREMENTS.map((requirement) => ({
                ...requirement,
                met: requirement.test(newPassword || ''),
            })),
        [newPassword]
    );

    const togglePasswordVisibility = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);

        try {
            const response = await usersService.changePassword(clinicId, userId, {
                oldPassword: data.oldPassword,
                newPassword: data.newPassword,
            });

            if (response.status === 200) {
                reset();
                toast.success('Password updated successfully. Logging out in 3 seconds...');
                setTimeout(() => {
                    handleLogout();
                }, 3000);
            } else {
                const errorMessage = response.data?.message || 'Failed to update password. Please try again.';
                toast.error(errorMessage);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                'An error occurred. Please check your connection and try again.';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="password-update-layout">
            <div className="password-update-panel-wrap">
            <div className="ecx-page-card password-update-panel">
                <div className="ecx-page-panel__header">
                    <div className="d-flex align-items-center gap-2">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-key" />
                        </span>
                        <div>
                            <h5 className="mb-0 fw-semibold text-dark">Change Password</h5>
                            <p className="mb-0 small text-muted">Enter your current password, then choose a new one</p>
                        </div>
                    </div>
                </div>

                <div className="ecx-page-panel__body">
                    <div className="password-update-panel__intro">
                        <span className="password-update-panel__intro-icon" aria-hidden="true">
                            <i className="bi bi-shield-lock" />
                        </span>
                        <div>
                            <h6 className="password-update-panel__intro-title">Secure your account</h6>
                            <p className="password-update-panel__intro-text">
                                After a successful password change, you will be signed out and asked to log in again
                                with your new password.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="password-update-form-grid">
                            <PasswordField
                                label="Current Password"
                                htmlFor="oldPassword"
                                error={errors.oldPassword?.message}
                                show={showPassword.oldPassword}
                                onToggle={() => togglePasswordVisibility('oldPassword')}
                                register={register}
                                validation={{
                                    required: 'Current password is required',
                                    minLength: {
                                        value: 8,
                                        message: 'Password must be at least 8 characters',
                                    },
                                }}
                            />

                            <PasswordField
                                label="New Password"
                                htmlFor="newPassword"
                                error={errors.newPassword?.message}
                                show={showPassword.newPassword}
                                onToggle={() => togglePasswordVisibility('newPassword')}
                                register={register}
                                validation={{
                                    required: 'New password is required',
                                    minLength: {
                                        value: 8,
                                        message: 'Password must be at least 8 characters',
                                    },
                                    validate: {
                                        hasUpperCase: (value) =>
                                            /[A-Z]/.test(value) || 'Must contain at least one uppercase letter',
                                        hasLowerCase: (value) =>
                                            /[a-z]/.test(value) || 'Must contain at least one lowercase letter',
                                        hasNumber: (value) =>
                                            /[0-9]/.test(value) || 'Must contain at least one number',
                                        notSameAsOld: (value) =>
                                            value !== oldPassword || 'New password must be different from current password',
                                    },
                                }}
                            >
                                {newPassword && (
                                    <div className="password-update-strength">
                                        <div className="password-update-strength__bar">
                                            <div
                                                className={`password-update-strength__fill ${passwordStrength.className}`}
                                                style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                                            />
                                        </div>
                                        <span className="password-update-strength__label">
                                            Strength: {passwordStrength.label}
                                        </span>
                                    </div>
                                )}
                            </PasswordField>

                            <PasswordField
                                label="Confirm New Password"
                                htmlFor="confirmPassword"
                                error={errors.confirmPassword?.message}
                                show={showPassword.confirmPassword}
                                onToggle={() => togglePasswordVisibility('confirmPassword')}
                                register={register}
                                validation={{
                                    required: 'Please confirm your new password',
                                    validate: (value) => value === newPassword || 'Passwords do not match',
                                }}
                            />
                        </div>

                        <div className="password-update-form__footer">
                            <ClinicSettingsFormFooter
                                isSaving={isSubmitting}
                                submitLabel="Update Password"
                            />
                        </div>
                    </form>
                </div>
            </div>
            </div>

            <div className="ecx-page-card password-update-requirements">
                <div className="ecx-page-panel__header">
                    <div className="d-flex align-items-center gap-2">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-info-circle" />
                        </span>
                        <div>
                            <h5 className="mb-0 fw-semibold text-dark">Password Requirements</h5>
                            <p className="mb-0 small text-muted">Your new password should meet the following criteria</p>
                        </div>
                    </div>
                </div>

                <div className="ecx-page-panel__body">
                    <ul className="password-update-requirements__list">
                        {requirementStatus.map((requirement) => (
                            <li
                                key={requirement.key}
                                className={`password-update-requirements__item ${
                                    requirement.met ? 'is-met' : ''
                                }`}
                            >
                                <i className={`bi ${requirement.met ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                                <span>{requirement.label}</span>
                            </li>
                        ))}
                        <li
                            className={`password-update-requirements__item ${
                                newPassword && oldPassword && newPassword !== oldPassword ? 'is-met' : ''
                            }`}
                        >
                            <i
                                className={`bi ${
                                    newPassword && oldPassword && newPassword !== oldPassword
                                        ? 'bi-check-circle-fill'
                                        : 'bi-circle'
                                }`}
                            />
                            <span>Different from current password</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PasswordUpdateComponent;
